import React, { useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import get from 'lodash/get';
import { Button, ComboBox, Breadcrumb, Alert } from '@boozallen-uip/uswds-react-core'
import { useNavigate, useLocation } from 'react-router-dom';
import PageHeader from '../../components/PageHeader/PageHeader.component';
import DashboardList from './DashboardList/DashboardList.component';
import { PageSubHeader, SectionContainer } from '../shared.styled';
import './VendorDashboard.page.scss';
import { UserContext } from '../UserContext';
import AccessStatus from '../../components/AccessStatus.component';
import useProduct from '../../hooks/useProduct.hook';
import {
  ACCESS_DENIED,
  ACCESS_LEVEL_ADMIN,
  ACCESS_LEVEL_USER,
  ACCESS_NO_LOGIN,
  ACCESS_WAITING,
  BASE_TITLE,
  HOME_URL,
  VENDOR_DASHBOARD_URL
} from '../../utlities/core.constants';

const DASHBOARD_INTRO_BUYER_USER =
  'Welcome to your dashboard. From here, you can view products with link alerts, or view all listed products.';
const DASHBOARD_INTRO_BUYER_ADMIN =
  "Welcome to your dashboard. From here, you can manage your organization's user requests and access. You can also view products with link alerts, or view all listed products.";
const DASHBOARD_INTRO_VENDOR_ADMIN =
  "Welcome to your dashboard. From here, you can add new products and view existing product listings. You can also manage user requests and access, along with your Agency's Overview page.";
const DASHBOARD_INTRO_VENDOR_EDITOR =
  "Welcome to your dashboard. From here, you can add new products and view existing product listings. You can also manage your Agency's Overview page.";
const DASHBOARD_INTRO_SUPER_USER =
  'Welcome to your dashboard. From here, you can manage user requests and access.';

const css = `
.usa-label { display: none}
`;
export default function VendorDashboard() {
  const { hasPendingRequestAccess, checkEntityAccessLevel, hasEntityAccess, userEntity, isLoggedIn, isSuperUser, user } = useContext(UserContext);
  const { fetchFilteredProductAll } = useProduct();
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [successMessage, setSuccessMessage] = useState(null);
  const productComboBoxRef = useRef(null);
  const normalizeProducts = useCallback((response) => (Array.isArray(response) ? response : []), []);

  document.title = `${BASE_TITLE} - Dashboard`;

  useEffect(() => {
    // Check for success message from navigation state
    if (location.state?.success && location.state?.message) {
      setSuccessMessage(location.state.message);

      // Clear the location state to prevent showing banner on refresh
      window.history.replaceState({}, document.title);

      // Auto-dismiss after 5 seconds
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [location]);

  useEffect(() => {
    (async () => {
      if (user?.vendorId) {
        // Vendors see only their products, including private/draft listings.
        const fetchedProducts = await fetchFilteredProductAll('', '', '', user.vendorId, true);
        setProducts(normalizeProducts(fetchedProducts));
      } else {
        // Agency users see all products
        const fetchedProducts = await fetchFilteredProductAll('', '', '');
        setProducts(normalizeProducts(fetchedProducts));
      }
    })();
  }, [user?.vendorId, fetchFilteredProductAll, normalizeProducts]);

  const resolveProductId = useCallback((productName) => {
    if (!productName) return "";
    const match = products.find(
      (product) => product.name?.toLowerCase() === productName.toLowerCase()
    );
    return match?.id ?? match?.Id ?? get(match, "_id") ?? "";
  }, [products]);

  const productOptions = useMemo(
    () => products.map((product) => product.name).filter(Boolean),
    [products]
  );

  const handleProductSelection = useCallback((event) => {
    const selectedName = event?.detail?.value ?? "";
    setSelectedProductId(resolveProductId(String(selectedName).trim()));
  }, [resolveProductId]);

  const handleGoToProduct = useCallback(() => {
    let productId = selectedProductId;
    if (!productId) {
      const typedValue = productComboBoxRef.current
        ?.querySelector('input')
        ?.value;
      productId = resolveProductId(String(typedValue ?? "").trim());
    }
    if (productId) {
      navigate(`/product-details/${productId}`);
    }
  }, [navigate, resolveProductId, selectedProductId]);

  const dashboardIntroText = useMemo(() => {
    if (isSuperUser) {
      return DASHBOARD_INTRO_SUPER_USER;
    }
    if (user?.agencyId && user.accessLevel === ACCESS_LEVEL_ADMIN) {
      return DASHBOARD_INTRO_BUYER_ADMIN;
    }
    if (user?.agencyId) {
      return DASHBOARD_INTRO_BUYER_USER;
    }
    if (user?.vendorId && user.accessLevel === ACCESS_LEVEL_ADMIN) {
      return DASHBOARD_INTRO_VENDOR_ADMIN;
    }
    if (user?.vendorId && user.accessLevel === ACCESS_LEVEL_USER) {
      return DASHBOARD_INTRO_VENDOR_EDITOR;
    }
    return DASHBOARD_INTRO_BUYER_USER;
  }, [isSuperUser, user]);

  if (!isLoggedIn()) {
    return (
      <AccessStatus access={ACCESS_NO_LOGIN} />
    )
  }

  if (!checkEntityAccessLevel() || !hasEntityAccess()) {
    return (
      <AccessStatus access={ACCESS_DENIED} />
    )
  }

  if (hasPendingRequestAccess) {
    return (
      <AccessStatus access={ACCESS_WAITING} />
    )
  }

  if (isSuperUser) {
    return (
      <div>
        <PageHeader imageUrl="/page-banners/account_hero_2x.webp" backgroundSize='2800px 391px' className='2xl:!bg-cover'>
          Welcome, {user?.name || user?.email}
        </PageHeader>
        <SectionContainer>
          <div className='mb-6'>
            <Breadcrumb links={[{"href": HOME_URL, "text": "Home"}, {"href": VENDOR_DASHBOARD_URL, "text": "Dashboard"}]} />
          </div>
          <PageSubHeader>Admin Dashboard</PageSubHeader>
          <p className='leading-normal'>
            {dashboardIntroText}
          </p>
          <DashboardList />
        </SectionContainer>
      </div>
    );
  }

  if (!userEntity) {
    return null;
  }

  if (user.vendorId) {
    return (
      <div>
        <PageHeader imageUrl="/page-banners/account_hero_2x.webp" backgroundSize='2800px 391px' className='2xl:!bg-cover'>
          Welcome, {user?.name ||  user?.email}
        </PageHeader>
        <SectionContainer>
          {successMessage && (
            <div className='mb-4'>
              <Alert type="success" heading="Success" dismissible onDismiss={() => setSuccessMessage(null)}>
                {successMessage}
              </Alert>
            </div>
          )}
          <div className='mb-6'>
            <Breadcrumb links={[{"href": HOME_URL, "text": "Home"}, {"href": VENDOR_DASHBOARD_URL, "text": "Dashboard"}]} />
          </div>
          <PageSubHeader>
            {userEntity?.entity?.name} ACR Repository Dashboard
          </PageSubHeader>
          <p className='leading-normal'>
            {dashboardIntroText}
          </p>
          <div className='pb-10'>
            <div>
              View or edit existing product listing:
            </div>
            <div className='grid grid-cols-12'>
              <style>{css}</style>
              <div className='inline-block mr-2 col-span-4' ref={productComboBoxRef}>
                <ComboBox
                  className='[&>.usa-label]:mt-0'
                  placeholder='Select product...'
                  id="search"
                  options={productOptions}
                  onChange={handleProductSelection} />
              </div>
              <div className='flex pt-2'>
                <Button onClick={handleGoToProduct}>Go</Button>
              </div>
            </div>
          </div>
          <DashboardList />
        </SectionContainer>
      </div>
    );
  }

  return (
    <div>
      <PageHeader imageUrl="/page-banners/account_hero_2x.webp" backgroundSize='2800px 391px' className='2xl:!bg-cover'>
        <span>Welcome, {user?.name ||  user?.email}</span>
      </PageHeader>
      <SectionContainer>
        {successMessage && (
          <div className='mb-4'>
            <Alert type="success" heading="Success" dismissible onDismiss={() => setSuccessMessage(null)}>
              {successMessage}
            </Alert>
          </div>
        )}
        <div className='mb-6'>
          Home &gt; Dashboard
        </div>
        <PageSubHeader>
          {userEntity?.entity?.name} ACR Repository Dashboard
        </PageSubHeader>
        <p className='leading-normal'>
          {dashboardIntroText}
        </p>
        <div className='pb-10'>
          <div>
            View existing product listing:
          </div>
          <div className='grid grid-cols-12'>
            <style>{css}</style>
            <div className='inline-block mr-2 col-span-4' ref={productComboBoxRef}>
              <ComboBox
                className='[&>.usa-label]:mt-0'
                placeholder='Select product...'
                id="search"
                options={productOptions}
                onChange={handleProductSelection} />
            </div>
            <div className='flex pt-2'>
              <Button onClick={handleGoToProduct}>Go</Button>
            </div>
          </div>
        </div>
        <DashboardList />
      </SectionContainer>
    </div>
  );
}
